/**
 * useRosterController — the board-first Shift Planner's brain, shared by both
 * homes (the Home detail pane and the standalone apps/roster page) so the two
 * surfaces cannot drift.
 *
 * What changed, and why it is not a refactor. This controller used to fold a
 * fenced `shift_commands` block out of the session transcript and call
 * `POST /sessions/{id}/generate-roster` for the solve. Neither exists:
 *
 *   - The roster is `GET /shifts`. A chat log is not a roster — it cannot be
 *     read by someone who was not in the conversation, and two clients folding
 *     the same history could disagree about what the ward is working.
 *   - The team is `GET /teams/{id}/members`. A team is PEOPLE; a location is a
 *     place, and the session carries neither.
 *   - The PROPOSAL is the ward's roster PLAN, whose items are what a lead is
 *     about to publish. Publishing is `POST /plans/roster/{id}:publish`, and it
 *     creates no shifts.
 *   - The SOLVE runs through the roster agent's planner tools.
 *
 * Writes are SERVER-AUTHORITATIVE, not optimistic. A shift rewrites ACL grants,
 * so a provisional cell would tell a lead someone holds access they do not.
 * Every mutation invalidates the roster reads and the board re-derives.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import type { SessionResponse } from '@/api/types_session'
import { errorToast } from '@/hooks/errorToast'
import { sessionKeys, type SessionDetail } from '@/hooks/useSessions'
import { useStreamingMessage } from '@/hooks/useStreamingChat'
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend'
import { useAuthStore } from '@/stores/auth'
import { AGENT_SLUG } from '../../agent'
import {
  rosterKeys,
  useCancelRosterShift,
  usePublishRosterPlan,
  useRevokeRosterPlan,
  useRosterOccurrences,
  useRosterPlan,
  useRosterShifts,
  useRosterTeamMembers,
  useRosterTeams,
} from '@/hooks/useRoster'
import type { TeamResponse } from '@/api/endpoints/teams'
import {
  planItemsToEntries,
  shiftsToEntries,
  type RosterEntry,
} from '@/components/roster/rosterEntries'

import { useRosterHorizon, type RosterHorizon } from './useRosterHorizon'
import { DEFAULT_HORIZONS, addDays, parseIso } from './rosterModel'

/** One board row: a person the roster may schedule. */
export interface RosterTeamMember {
  /** Platform user id — the value a shift's `user` carries. */
  id: string
  /** Display label; the user id when the platform resolved no name. */
  name: string
  /** A code from `config.staffing.qualifications`; empty = unmapped. */
  qualification: string
  role: string
}

export interface RosterController {
  sessionId: string
  isAuthor: boolean
  /** The planner chat's send — mints the session on the first message when
   *  the board is session-less (useDeferredFirstSend). */
  sendChat: (content: string) => void
  chatPending: boolean
  horizon: RosterHorizon

  /** Which roster is being planned. A shift belongs to a TEAM's roster. */
  teams: TeamResponse[]
  teamId: string | undefined
  setTeamId: (id: string) => void
  teamsLoading: boolean

  /**
   * What the board draws: the open draft's items while a draft is in play,
   * otherwise the window's shift rows.
   */
  entries: RosterEntry[]
  hasRoster: boolean
  windowDates: string[]

  // team (every schedulable teammate → a row, even with zero shifts)
  team: RosterTeamMember[]
  teamLoading: boolean
  teamError: boolean
  teamReady: boolean
  refetchTeam: () => void

  shiftsLoading: boolean
  shiftsError: boolean
  shiftsFetching: boolean
  shiftsReady: boolean
  refetchShifts: () => void

  /** The ward's roster plan, or null when nobody has planned this ward yet. */
  planId: string | null
  /** True while that plan is a draft: the board shows it and offers publish. */
  hasDraft: boolean
  /**
   * True when the occurrence read failed. The plan still drew, so the grid is
   * real — but every cell falls back to its bare code, which is exactly what an
   * unpublished plan looks like. Without saying so, a lead reads a working
   * roster as one nobody has published.
   */
  occurrencesUnavailable: boolean
  /** The plan's items as board cells: what publishing would make the roster. */
  proposal: RosterEntry[]
  proposedCount: number
  isPublishing: boolean
  publish: () => void
  isDiscarding: boolean
  discard: () => void

  // manual edits
  isSaving: boolean
  clearCell: (userId: string, date: string) => void

  // the agent (chat) — where the solve lives
  streaming: ReturnType<typeof useStreamingMessage>
}

export function useRosterController(
  session: SessionDetail | undefined,
  onSessionCreated?: (session: SessionResponse) => void,
): RosterController {
  const { t } = useTranslation()
  const sessionId = session?.session.id ?? ''
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((s) => s.user?.id ?? '')
  const streaming = useStreamingMessage(sessionId)
  const deferred = useDeferredFirstSend({
    sessionId: session?.session.id,
    agentSlug: AGENT_SLUG,
    sendMessage: streaming.sendMessage,
    onCreated: onSessionCreated,
  })

  // The horizon options are the board's own: a control's option list is a
  // frontend affordance, not planning policy.
  const horizon = useRosterHorizon(DEFAULT_HORIZONS)

  // Viewer-vs-author (the server's ACL is the real boundary — this only hides
  // dead controls). A session-less board is the caller's own surface
  // (TeamAccessBoardConnected precedent).
  const isAuthor = session ? session.session.created_by.performer === currentUserId : true

  // Which roster. The session names no team, so the caller's own teams are the
  // candidates and the first is the default; a lead of several picks.
  const teamsQuery = useRosterTeams()
  const teams = useMemo(() => teamsQuery.data ?? [], [teamsQuery.data])
  const [chosenTeamId, setChosenTeamId] = useState<string | undefined>(undefined)
  const teamId = chosenTeamId ?? teams[0]?.id

  const membersQuery = useRosterTeamMembers(teamId)
  const team = useMemo<RosterTeamMember[]>(
    () =>
      (membersQuery.data ?? []).map((m) => ({
        id: m.user,
        name: m.user_ref?.display ?? m.user,
        qualification: m.qualification,
        role: m.role,
      })),
    [membersQuery.data],
  )

  // The window on the TIMESTAMPS. `horizon.to` is the last roster-day LABEL, so
  // the exclusive upper bound is the start of the day after it — a shift
  // labelled on the last day must be inside the window, not on its edge.
  const windowFrom = useMemo(() => parseIso(horizon.from).toISOString(), [horizon.from])
  const windowTo = useMemo(() => parseIso(addDays(horizon.to, 1)).toISOString(), [horizon.to])
  const shiftsQuery = useRosterShifts({ teamId, from: windowFrom, to: windowTo })

  const names = useMemo(() => new Map(team.map((m) => [m.id, m.name])), [team])
  const entries = useMemo(
    () => shiftsToEntries(shiftsQuery.data, names),
    [shiftsQuery.data, names],
  )


  // The PROPOSAL is the ward's roster plan. One plan per ward at a time, so
  // there is no longer a set of drafts to agree on an id: the plan IS the unit
  // that publishes or is withdrawn.
  //
  // The ward comes from the team, because a team states the units it staffs and
  // the board is already on a team. A team that staffs several units has no one
  // ward, and this board plans one — so the first is taken and the case is
  // visible rather than silently merged.
  const wardId = useMemo(() => {
    const staffed = teams.find((tm) => tm.id === teamId)?.staffed_locations ?? []
    return staffed.length > 0 ? staffed[0] : undefined
  }, [teams, teamId])
  const planQuery = useRosterPlan(wardId)
  const plan = planQuery.data ?? null
  // What each cell shows and what happened to it, both from the planner: the
  // display is resolved from the category's declaration, and the execution
  // state is folded from the plan's change log. A plan not in force has
  // neither, and its cells carry their bare code.
  const { occurrences, error: occurrencesError } = useRosterOccurrences(plan)
  const inForce = plan?.status === 'active'
  const proposal = useMemo(
    () => (plan ? planItemsToEntries(plan.id, plan.items, names, occurrences, inForce) : []),
    [plan, names, occurrences, inForce],
  )

  // THE PLAN IS THE ROSTER, whenever there is one. A draft is what the ward is
  // being asked to publish; an active plan's items are what it is working,
  // because publishing creates no shift row anywhere — a shift IS a roster
  // item's occurrence. Drawing shift rows for an active plan showed a board
  // with no labels, times or colours, since those reach a cell only through
  // its occurrence. Shift rows remain for a ward that has no plan at all.
  const hasDraft = plan?.status === 'draft'
  const boardEntries = plan ? proposal : entries
  const hasRoster = boardEntries.length > 0 || hasDraft

  const cancelShift = useCancelRosterShift()
  const publishRosterPlan = usePublishRosterPlan()
  const revokeRosterPlan = useRevokeRosterPlan()

  const reportWriteFailure = useCallback(
    (context: string, fallback: string) => (err: ApiError) => {
      errorToast({
        context,
        description:
          err instanceof ApiError && err.status >= 400 && err.status < 500 && err.message
            ? err.message
            : fallback,
        errorCode: err instanceof ApiError ? err.code : undefined,
        error: err,
      })
    },
    [],
  )

  // Cancel, never delete: a published shift is a fact the ward acted on, and
  // the record of its cancellation is what an audit reads.
  const clearCell = useCallback(
    (userId: string, date: string) => {
      const existing = entries.find((e) => e.date === date && e.nurseId === userId)
      if (!existing) return
      cancelShift.mutate(existing.id, {
        onError: reportWriteFailure('rosterClear', t('roster.editFailed', 'Could not save the change. Please try again.')),
      })
    },
    [entries, cancelShift, reportWriteFailure, t],
  )

  const publish = useCallback(() => {
    if (!plan) return
    publishRosterPlan.mutate({ planId: plan.id, version: plan.version }, {
      onError: reportWriteFailure('rosterPublish', t('roster.publishFailed', 'Could not publish the roster. Please try again.')),
    })
  }, [plan, publishRosterPlan, reportWriteFailure, t])

  const discard = useCallback(() => {
    if (!plan) return
    revokeRosterPlan.mutate({ planId: plan.id, version: plan.version }, {
      onError: reportWriteFailure('rosterDiscard', t('roster.discardFailed', 'Could not discard the proposal. Please try again.')),
    })
  }, [plan, revokeRosterPlan, reportWriteFailure, t])

  // When an agent turn finishes it may have committed a proposal, so re-read the
  // roster. This is the ONLY thing the transcript is used for now: a signal that
  // the server state may have moved, never as the state itself.
  const wasStreaming = useRef(false)
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true
    } else if (wasStreaming.current) {
      wasStreaming.current = false
      if (sessionId) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(sessionId) })
      }
      void queryClient.invalidateQueries({ queryKey: rosterKeys.all })
    }
  }, [streaming.isStreaming, sessionId, queryClient])

  return {
    sessionId,
    isAuthor,
    sendChat: (content: string) => void deferred.send(content),
    chatPending: streaming.isStreaming || deferred.isPending,
    horizon,
    teams,
    teamId,
    setTeamId: setChosenTeamId,
    teamsLoading: teamsQuery.isLoading,
    entries: boardEntries,
    hasRoster,
    windowDates: horizon.days,
    team,
    teamLoading: membersQuery.isLoading,
    teamError: membersQuery.isError,
    teamReady: membersQuery.data !== undefined,
    refetchTeam: () => void membersQuery.refetch(),
    shiftsLoading: shiftsQuery.isLoading,
    shiftsError: shiftsQuery.isError,
    shiftsFetching: shiftsQuery.isFetching,
    shiftsReady: shiftsQuery.data !== undefined,
    refetchShifts: () => void shiftsQuery.refetch(),
    planId: plan?.id ?? null,
    hasDraft,
    occurrencesUnavailable: !!occurrencesError,
    proposal,
    proposedCount: hasDraft ? proposal.length : 0,
    isPublishing: publishRosterPlan.isPending,
    publish,
    isDiscarding: revokeRosterPlan.isPending,
    discard,
    isSaving: cancelShift.isPending,
    clearCell,
    streaming,
  }
}
